/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Roles & Permissions Contract — endpoints for the Admin > Roles & Permissions
 * tab. Backs the data-driven custom-role system (separate from Better Auth's
 * code-level access-control DSL).
 *
 * Conceptual model
 * ----------------
 *  - A `Permission` is a feature key the app gates on
 *    (`feature:chat`, `feature:calendar`, `feature:documents`).
 *    These keys mirror `OrgFeatures` in `@bavard/types-common/org-plan`.
 *  - A `Role` is either a SYSTEM default (`organizationId = null`,
 *    `isSystem = true`) edited only by platform SUPERUSERs, or an
 *    ORG-scoped row (`organizationId` set, `isSystem = false`) editable
 *    by org owners/admins.
 *  - Each `Role` carries a permission map (`feature:chat -> true|false`)
 *    that the frontend resolves against the org plan to render the
 *    permission grid (plan-disabled features always render as `na`).
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";

// ---------------------------------------------------------------------------
// Domain schemas
// ---------------------------------------------------------------------------

/**
 * Permission key — namespaced as `feature:<orgFeaturesKey>`. Today these
 * mirror `OrgFeatures` (chat / calendar / documents). Use a
 * permissive `z.string()` here so adding a new feature key is a server-only
 * change (no contract bump needed); the seeder is the source of truth.
 */
export const PermissionKeySchema = z.string().min(1).max(64);
export type PermissionKey = z.infer<typeof PermissionKeySchema>;

/** Catalog row for the feature catalog endpoint. */
export const PermissionSchema = z.object({
	id: z.string(),
	key: PermissionKeySchema,
	description: z.string().nullable(),
	createdAt: z.string(),
});
export type Permission = z.infer<typeof PermissionSchema>;

/**
 * Permissions map — `<permissionKey> -> granted`. Always returns the full
 * set of seeded permissions so the frontend can render the grid without a
 * second round trip; missing keys default to `false` on writes.
 */
export const PermissionsMapSchema = z.record(PermissionKeySchema, z.boolean());
export type PermissionsMap = z.infer<typeof PermissionsMapSchema>;

/**
 * Role row returned by the list endpoint. Includes the permissions map and
 * the live member count so the frontend sidebar can render the badge
 * without a second query.
 */
export const RoleSchema = z.object({
	id: z.string(),
	organizationId: z.string().nullable(),
	name: z.string().min(1).max(64),
	description: z.string().nullable(),
	isSystem: z.boolean(),
	memberCount: z.number().int().min(0),
	permissions: PermissionsMapSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Role = z.infer<typeof RoleSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

/**
 * `organizationId` semantics:
 *  - When set: returns org-scoped rows + visible system defaults.
 *  - When null: SUPERUSER-only, returns the global system defaults.
 */
const ListRolesInputSchema = z.object({
	organizationId: z.string().nullable(),
});

const ListRolesOutputSchema = z.object({
	items: z.array(RoleSchema),
});

const CreateRoleInputSchema = z.object({
	organizationId: z.string().nullable(),
	name: z.string().min(1).max(64),
	description: z.string().max(256).optional(),
	permissions: PermissionsMapSchema,
});

const CreateRoleOutputSchema = RoleSchema;

const UpdateRoleInputSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1).max(64).optional(),
	description: z.string().max(256).nullable().optional(),
	permissions: PermissionsMapSchema.optional(),
});

const UpdateRoleOutputSchema = RoleSchema;

const DeleteRoleInputSchema = z.object({
	id: z.string().min(1),
});

const DeleteRoleOutputSchema = z.object({
	id: z.string(),
	deleted: z.boolean(),
});

/**
 * Org-member row for the role-assignment dialog. A scoped, non-paginated
 * variant of `superadmin.listMembers` that an org admin/owner can call
 * for THEIR OWN org. SUPERUSER bypasses the gate (any org).
 *
 * `id` is `Member.id` (the Better Auth org-Member row pkey, NOT User.id) —
 * this matches the input shape that `roles.assignMember` / `unassignMember`
 * expect.
 */
const OrgMemberSchema = z.object({
	id: z.string(),
	userId: z.string(),
	name: z.string().nullable(),
	/**
	 * Email is `String?` on the underlying `User` model (optional in
	 * Better Auth — anonymous users have no email until they upgrade),
	 * so the contract reflects that honestly.
	 */
	email: z.string().nullable(),
	/** Better Auth org role: owner / admin / member. */
	orgRole: z.string(),
	/** `Member.customRoleId` — null when no custom role is assigned. */
	customRoleId: z.string().nullable(),
});

const ListOrgMembersInputSchema = z.object({
	organizationId: z.string().min(1),
});

const ListOrgMembersOutputSchema = z.object({
	items: z.array(OrgMemberSchema),
});

const AssignMemberInputSchema = z.object({
	memberId: z.string().min(1),
	roleId: z.string().min(1),
});

const AssignMemberOutputSchema = z.object({
	memberId: z.string(),
	roleId: z.string().nullable(),
});

const UnassignMemberInputSchema = z.object({
	memberId: z.string().min(1),
});

const UnassignMemberOutputSchema = z.object({
	memberId: z.string(),
	roleId: z.null(),
});

const ListPermissionsInputSchema = z.object({}).optional();

const ListPermissionsOutputSchema = z.object({
	items: z.array(PermissionSchema),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const rolesContract = {
	list: oc
		.route({
			method: "GET",
			path: "/api/roles",
			summary: "List custom roles for an org (or global system defaults)",
			tags: ["Roles"],
		})
		.input(ListRolesInputSchema)
		.output(ListRolesOutputSchema)
		.errors({ ...commonErrors }),

	create: oc
		.route({
			method: "POST",
			path: "/api/roles",
			summary: "Create a new custom role + permission rows in a transaction",
			tags: ["Roles"],
		})
		.input(CreateRoleInputSchema)
		.output(CreateRoleOutputSchema)
		.errors({ ...commonErrors }),

	update: oc
		.route({
			method: "POST",
			path: "/api/roles/update",
			summary: "Update a role and/or its permissions atomically",
			tags: ["Roles"],
		})
		.input(UpdateRoleInputSchema)
		.output(UpdateRoleOutputSchema)
		.errors({ ...commonErrors }),

	delete: oc
		.route({
			method: "POST",
			path: "/api/roles/delete",
			summary: "Delete a custom role (system roles cannot be deleted)",
			tags: ["Roles"],
		})
		.input(DeleteRoleInputSchema)
		.output(DeleteRoleOutputSchema)
		.errors({ ...commonErrors }),

	listOrgMembers: oc
		.route({
			method: "GET",
			path: "/api/roles/org-members",
			summary:
				"List members of an organization with their custom-role assignment",
			tags: ["Roles"],
		})
		.input(ListOrgMembersInputSchema)
		.output(ListOrgMembersOutputSchema)
		.errors({ ...commonErrors }),

	assignMember: oc
		.route({
			method: "POST",
			path: "/api/roles/assign-member",
			summary: "Assign a custom role to a member",
			tags: ["Roles"],
		})
		.input(AssignMemberInputSchema)
		.output(AssignMemberOutputSchema)
		.errors({ ...commonErrors }),

	unassignMember: oc
		.route({
			method: "POST",
			path: "/api/roles/unassign-member",
			summary: "Clear the custom role assignment on a member",
			tags: ["Roles"],
		})
		.input(UnassignMemberInputSchema)
		.output(UnassignMemberOutputSchema)
		.errors({ ...commonErrors }),
};

export const permissionsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/api/permissions",
			summary: "List the seeded feature permission catalog",
			tags: ["Permissions"],
		})
		.input(ListPermissionsInputSchema)
		.output(ListPermissionsOutputSchema)
		.errors({ ...commonErrors }),
};

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------

export type RolesContract = typeof rolesContract;
export type PermissionsContract = typeof permissionsContract;

export type ListRolesInput = z.infer<typeof ListRolesInputSchema>;
export type ListRolesOutput = z.infer<typeof ListRolesOutputSchema>;
export type CreateRoleInput = z.infer<typeof CreateRoleInputSchema>;
export type CreateRoleOutput = z.infer<typeof CreateRoleOutputSchema>;
export type UpdateRoleInput = z.infer<typeof UpdateRoleInputSchema>;
export type UpdateRoleOutput = z.infer<typeof UpdateRoleOutputSchema>;
export type DeleteRoleInput = z.infer<typeof DeleteRoleInputSchema>;
export type DeleteRoleOutput = z.infer<typeof DeleteRoleOutputSchema>;
export type ListOrgMembersInput = z.infer<typeof ListOrgMembersInputSchema>;
export type ListOrgMembersOutput = z.infer<typeof ListOrgMembersOutputSchema>;
export type OrgMember = z.infer<typeof OrgMemberSchema>;
export type AssignMemberInput = z.infer<typeof AssignMemberInputSchema>;
export type AssignMemberOutput = z.infer<typeof AssignMemberOutputSchema>;
export type UnassignMemberInput = z.infer<typeof UnassignMemberInputSchema>;
export type UnassignMemberOutput = z.infer<typeof UnassignMemberOutputSchema>;
export type ListPermissionsInput = z.infer<typeof ListPermissionsInputSchema>;
export type ListPermissionsOutput = z.infer<typeof ListPermissionsOutputSchema>;

// Re-export the inputs/outputs schemas (the domain schemas above are
// already exported via `export const` declarations).
export {
	AssignMemberInputSchema,
	AssignMemberOutputSchema,
	CreateRoleInputSchema,
	CreateRoleOutputSchema,
	DeleteRoleInputSchema,
	DeleteRoleOutputSchema,
	ListOrgMembersInputSchema,
	ListOrgMembersOutputSchema,
	ListPermissionsInputSchema,
	ListPermissionsOutputSchema,
	ListRolesInputSchema,
	ListRolesOutputSchema,
	OrgMemberSchema,
	UnassignMemberInputSchema,
	UnassignMemberOutputSchema,
	UpdateRoleInputSchema,
	UpdateRoleOutputSchema,
};
